'use client'

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { STALE_TIMES, CACHE_TIMES } from '@/providers/QueryProvider'

// =====================================
// Query Keys
// =====================================
export const labKeys = {
  all: ['lab'] as const,
  
  // Lab results for clinic (inbox view)
  results: (clinicId: string) => 
    [...labKeys.all, 'results', clinicId] as const,
  resultsWithFilters: (clinicId: string, hideReviewed: boolean, page?: number) => 
    [...labKeys.results(clinicId), hideReviewed, page] as const,
  
  // Lab tests for patient
  patientTests: (patientId: string) => 
    [...labKeys.all, 'patient-tests', patientId] as const,
  
  // Lab test trends
  trends: (patientId: string, testName: string) => 
    [...labKeys.all, 'trends', patientId, testName] as const,
}

// =====================================
// Types
// =====================================
interface LabResult {
  id: string
  unique_key: string
  type: 'lab_test' | 'blood_test' | 'referral_response' | 'radiology_response' | 'incoming_referral'
  type_display: string
  patient_id: string
  patient_name: string
  title: string
  description: string
  status: 'pending' | 'completed' | 'reviewed'
  has_response: boolean
  date: string
  ordered_by_user_id?: string
  ordered_by_name?: string
  assigned_doctor_id?: string
  assigned_doctor_name?: string
  reviewed_by?: string
  reviewed_at?: string
  priority: string
  response_text?: string
  response_date?: string
  raw_data?: any
}

interface LabResultsResponse {
  success: boolean
  results: LabResult[]
  pagination?: {
    page: number
    pageSize: number
    total: number
    hasMore: boolean
  }
}

// =====================================
// Fetch Functions
// =====================================
async function fetchLabResults(
  clinicId: string,
  hideReviewed = true,
  page = 1,
  pageSize = 50
): Promise<LabResultsResponse> {
  const params = new URLSearchParams({
    clinic_id: clinicId,
    hide_reviewed: hideReviewed.toString(),
    page: page.toString(),
    page_size: pageSize.toString()
  })

  const response = await fetch(`/api/lab/results?${params}`)
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to fetch lab results')
  }
  return response.json()
}

async function fetchPatientLabTests(patientId: string, clinicId?: string) {
  const params = new URLSearchParams({ patient_id: patientId })
  if (clinicId) params.append('clinic_id', clinicId)

  const response = await fetch(`/api/lab-tests?${params}`)
  if (!response.ok) {
    throw new Error('Failed to fetch patient lab tests')
  }
  return response.json()
}

// =====================================
// Query Hooks
// =====================================

/**
 * Fetch lab results for clinic with caching
 * Uses infinite query for pagination
 */
export function useLabResults(
  clinicId: string,
  hideReviewed = true,
  enabled = true
) {
  return useQuery({
    queryKey: labKeys.resultsWithFilters(clinicId, hideReviewed),
    queryFn: () => fetchLabResults(clinicId, hideReviewed),
    enabled: enabled && !!clinicId,
    staleTime: STALE_TIMES.MEDICAL_RESULTS,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Fetch lab results with infinite scroll pagination
 */
export function useLabResultsInfinite(
  clinicId: string,
  hideReviewed = true,
  pageSize = 50,
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: labKeys.resultsWithFilters(clinicId, hideReviewed),
    queryFn: ({ pageParam = 1 }) => fetchLabResults(clinicId, hideReviewed, pageParam, pageSize),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination?.hasMore) {
        return (lastPage.pagination.page || 1) + 1
      }
      return undefined
    },
    enabled: enabled && !!clinicId,
    staleTime: STALE_TIMES.MEDICAL_RESULTS,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Fetch patient's lab tests
 */
export function usePatientLabTests(patientId: string, clinicId?: string, enabled = true) {
  return useQuery({
    queryKey: labKeys.patientTests(patientId),
    queryFn: () => fetchPatientLabTests(patientId, clinicId),
    enabled: enabled && !!patientId,
    staleTime: STALE_TIMES.MEDICAL_RESULTS,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Mark lab result as reviewed
 */
export function useMarkAsReviewed() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: {
      resultId: string
      resultType: string
      reviewedByUserId: string
      clinicId: string
    }) => {
      const response = await fetch('/api/lab/results/review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!response.ok) throw new Error('Failed to mark as reviewed')
      return response.json()
    },
    onSuccess: (_, variables) => {
      // Invalidate lab results cache
      queryClient.invalidateQueries({
        queryKey: labKeys.results(variables.clinicId)
      })
    }
  })
}

/**
 * Reassign lab result to another doctor
 */
export function useReassignLabResult() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: {
      resultId: string
      resultType: string
      newDoctorId: string
      clinicId: string
      reassignedByUserId: string
      reason?: string
    }) => {
      const response = await fetch('/api/lab/results/reassign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!response.ok) throw new Error('Failed to reassign')
      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: labKeys.results(variables.clinicId)
      })
    }
  })
}

/**
 * Prefetch lab results for faster navigation
 */
export function usePrefetchLabResults() {
  const queryClient = useQueryClient()
  
  return (clinicId: string, hideReviewed = true) => {
    queryClient.prefetchQuery({
      queryKey: labKeys.resultsWithFilters(clinicId, hideReviewed),
      queryFn: () => fetchLabResults(clinicId, hideReviewed),
      staleTime: STALE_TIMES.MEDICAL_RESULTS,
    })
  }
}
